import { changeGlobalTheme } from 'smbls'
import { mermaidUrl, currentChannel } from '@symbo.ls/channels'
import { options as a11yOptions } from '@symbo.ls/brand/designSystem/a11y.js'
import { resolveProjectParams } from '../../shared/functions/resolveProjectParams.js'

// Active indicator lives on the SVG sprite itself (filled vs outline)
// and on the icon's color — verified to render, unlike background, which
// this pipeline keeps stuck on the atomic-class value regardless of the
// rule specificity or `!important`.
const paintToggleActive = (el, active) => {
  if (!el?.node) return
  const iconNode = el.Icon?.node
  const useEl = iconNode?.querySelector('use')
  if (useEl) {
    useEl.setAttribute(
      'xlink:href',
      active ? '#sidebarLeftFill' : '#sidebarLeft'
    )
  }
  if (iconNode) {
    iconNode.style.color = active ? 'var(--color-title)' : ''
    iconNode.style.opacity = active ? '1' : '0.65'
  }
}

const getProjectConfig = (el) => {
  const doc = el.context?.document
  if (!doc?.getElementById) return null
  // Target whichever iframe is currently visible so the ThemeSwitcher
  // flips the theme of the app the user is looking at (project app in
  // `app` view, design-system app in `designSystem` view).
  const viewMode = el.getRootState?.()?.viewMode
  const id = viewMode === 'designSystem' ? 'preview-ds-iframe' : 'preview-app-iframe'
  return doc.getElementById(id)?.__projectConfig
}

// Dropdown open/close driven by root state.
// `DropdownField` alone renders the trigger shell; each specific dropdown
// guards its own `Dropdown` subtree with `if:` keyed off `openDropdown` on
// root state. `DropdownParent(Focus)` proved unreliable here because the
// `_op-0`/`_vis-hidden` atomic classes intercept opacity before the
// `:focus-within` rule can win, and inline style overrides fail the same
// way — something in the atomic-css pipeline short-circuits this element.
const dropdownTrigger = (key) => ({
  extends: 'DropdownField',
  // `DropdownField.tag` is `<button>`. Nesting `<section>` (the Dropdown)
  // inside `<button>` is invalid HTML and Chrome seems to force-hide
  // descendants regardless of inline `!important` styles — verified by
  // finding no CSS rule that should beat the inline but computed still
  // showed `opacity:0; visibility:hidden` on the dropdown. Override to a
  // plain `<div>` so the nested section is valid.
  tag: 'div',
  theme: 'quaternary',
  padding: 'X X X Z2',
  align: 'center start',
  gap: 'X',
  fontSize: 'Z2',
  round: 'C',
  // Anchor the absolutely-positioned `Dropdown` subtree to the trigger —
  // otherwise it snaps to the viewport's left edge.
  position: 'relative',
  onClick: (ev, el) => {
    if (ev.target.closest('[dropdown]')) return
    const s = el.getRootState()
    s.update({ openDropdown: s.openDropdown === key ? null : key })
  },
  Buttons: {
    margin: '- - - auto',
    Arrow: {
      theme: 'transparent',
    }
  }
})
const dropdownOpenIf = (key) => (el) => el.getRootState('openDropdown') === key
const closeDropdown = (el) => el.getRootState().update({ openDropdown: null })
// Don't inherit the default `Dropdown` component — its baked-in
// `opacity:0; visibility:hidden` atomic classes refuse to yield to even
// `!important` inline styles in this pipeline. Roll our own positioned
// panel and drive open/close via reactive `hide:` which DOMQL tracks.
const dropdownVisible = (key) => ({
  extends: null,
  attr: { dropdown: true },
  position: 'absolute',
  top: '110%',
  right: '0',
  zIndex: 1000,
  theme: 'quaternary',
  padding: 'Y',
  round: 'Z2',
  flow: 'column',
  gap: 'X',
  minWidth: 'F1',
  transition: 'B defaultBezier',
  transitionProperty: 'transform, opacity',
  hide: (el) => el.getRootState('openDropdown') !== key
})


export const PreviewNavbar = {
  extends: 'Navbar',
  // Hide the chrome when project data isn't loaded — the iframe Frame
  // doesn't exist, so the env switcher / sidebar toggle / sharing
  // affordances have nothing to operate on. PreviewErrorBoundary
  // renders its own minimal chrome (Logo + retry actions) instead.
  hide: (el, s) => !!s.root?.fetchError,
  gap: 'A2',
  userSelect: 'none',
  theme: 'common-box',
  margin: 'X2 X2 -',
  round: 'C1',
  padding: 'X X2',

  // Override the shared Logo's default `position: fixed`; in the preview
  // navbar the logo flows inline with the other chrome buttons (sibling
  // of SidebarToggle / EnvSwitcher) instead of pinning to the viewport.
  Logo: {
    position: 'relative',
    top: 'auto',
    left: 'auto',
    zIndex: 'auto'
  },

  // Canvas link — opens the project in the canvas editor. Resolves
  // owner/key from URL params (preview can be reached either via
  // `?owner=X&key=Y` query or `{owner}--{key}.preview.symbols.app`
  // subdomain). Targets `canvas.localhost:1355` when previewing on
  // localhost; `edit.symbols.app` otherwise, matching the URLs the
  // `smbls publish` CLI prints. Propagates `?env=…` so clicking from a
  // preview of `staging` lands on the same env in canvas.
  CanvasLink: {
    tag: 'a',
    attr: { title: 'Open in Canvas editor' },
    padding: 'Y2',
    round: 'C1',
    background: 'transparent',
    cursor: 'pointer',
    color: 'currentColor',
    align: 'center center',
    transition: 'B defaultBezier',
    transitionProperty: 'background, color, opacity',
    opacity: 0.65,
    ':hover': {
      background: 'rgba(255,255,255,0.04)',
      opacity: 1
    },
    href: (el, s) => {
      const { owner, key } = resolveProjectParams()
      if (!owner || !key) return '#'
      const isLocal = window.location.hostname.endsWith('.localhost') ||
        window.location.hostname === 'localhost'
      const origin = isLocal
        ? 'https://canvas.localhost:1355'
        : 'https://edit.symbols.app'
      const env = s?.root?.env || s?.env
      const qs = env && env !== 'development' ? `?env=${env}` : ''
      return `${origin}/${owner}/${key}${qs}`
    },
    target: '_blank',
    Icon: {
      extends: 'Icon',
      icon: 'layout',
      boxSize: 'A A'
    }
  },

  // Sidebar toggle — flips `root.sidebarOpen`. The explorer buttons
  // (Pages / Components / Design System) live inside `PreviewSidebar`
  // itself, not here.
  SidebarToggle: {
    // Rendered as a `<div role=button>` (not `<button>`) because Chrome's
    // UA form-control pipeline drops inline `!important` backgrounds on
    // native `<button>` elements in this context — same quirk we hit on
    // the dropdown visibility earlier. A styled div with role=button is
    // accessible and doesn't suffer the cascade override.
    tag: 'div',
    role: 'button',
    tabindex: '0',
    title: 'Toggle sidebar',
    padding: 'Y2',
    round: 'C1',
    background: 'transparent',
    cursor: 'pointer',
    color: 'currentColor',
    align: 'center center',
    transition: 'B defaultBezier',
    transitionProperty: 'background, color',
    ':hover': { background: 'rgba(255,255,255,0.04)' },
    // Paint the active state directly on the DOM node. Must use
    // `setProperty(..., 'important')` because the element's own atomic
    // `background: transparent` class beats inline styles in this
    // pipeline (same cascade quirk that forced the dropdown visibility
    // fix earlier).
    onRender: (el, s) => {
      paintToggleActive(el, !!s.root?.sidebarOpen)
    },
    onClick: (ev, el) => {
      const s = el.getRootState()
      const nextOpen = !s.sidebarOpen
      s.update({ sidebarOpen: nextOpen })
      // Defer paint past DOMQL's reactive pass — calling immediately
      // loses the race (the post-state repaint clears our inline).
      setTimeout(() => paintToggleActive(el, nextOpen), 0)
    },
    Icon: {
      extends: 'Icon',
      icon: 'sidebarLeft',
      boxSize: 'A A'
    }
  },

  // Environment switcher — sits on the left next to the sidebar toggle.
  // Each option's `enabled` state is read off the loaded project's
  // `environments[envKey].enabled` flag. Disabled envs (either not on
  // the plan or explicitly turned off for this project) render dimmed
  // with an "Upgrade" suffix; clicking them opens the upgrade URL
  // instead of firing a doomed fetch.
  EnvSwitcher: {
    ...dropdownTrigger('env'),
    Value: {
      Text: {
        text: (el, s) => {
          const env = s.env || 'development'
          const label = env.charAt(0).toUpperCase() + env.slice(1)
          return s.isLoadingProject ? `${label} · loading…` : label
        },
        opacity: (_, s) => s.isLoadingProject ? '0.6' : '1'
      }
    },
    Dropdown: {
      ...dropdownVisible('env'),
      ListInDropdown: {
        childrenAs: 'state',
        children: [
          { key: 'development', text: 'Development' },
          { key: 'production', text: 'Production' },
          { key: 'staging', text: 'Staging' }
        ],
        childProps: {
          // Flips `.isActive` class on the currently-selected env so
          // DropdownItem's existing cascade can light up the checkmark
          // icon + bold the label. Falls back to `development` when
          // root.env isn't yet set (initial render).
          isActive: (el, s) => (s.root?.env || 'development') === s.key,
          // Flips `.isDisabled` class when the env isn't enabled on the
          // loaded project — the class hook below dims it and opens the
          // upgrade URL on click instead of firing a doomed fetch.
          isDisabled: (el, s) => {
            const envs = s.root?.data?.environments
            if (!envs) return false
            const cfg = envs[s.key]
            return !cfg || cfg.enabled === false
          },
          '.isDisabled': {
            opacity: '0.5',
            cursor: 'not-allowed',
            ':hover': { opacity: '0.5' }
          },
          // Reactive checkmark — DropdownItem's default `& svg { opacity:
          // 0.5 }` cascade under `.isActive` gets outrun by Icon's own
          // `opacity: 0` atomic class (same specificity, later applied).
          // Setting opacity on the nested Icon here is what actually
          // flips the checkmark for the selected env.
          Icon: {
            opacity: (el, s) => (s.root?.env || 'development') === s.key ? '1' : '0'
          },
          // Append " · Upgrade" to the visible label when the env is
          // disabled. Targets the nested `Text` slot rather than the
          // button root — the root inherits `text` from the state, so
          // setting `text` here would double up the label.
          Text: {
            text: (el, s) => {
              const envs = s.root?.data?.environments
              const cfg = envs?.[s.key]
              const disabled = !!envs && (!cfg || cfg.enabled === false)
              return disabled ? `${s.text} · Upgrade` : (s.text || s.key)
            }
          },
          onClick: async (ev, el, s) => {
            closeDropdown(el)
            const envs = s.root?.data?.environments
            const cfg = envs?.[s.key]
            const disabled = !!envs && (!cfg || cfg.enabled === false)
            if (disabled) {
              // Take the user to the upgrade flow — same convention the
              // InNewTab button uses for its owner/key URL resolution.
              const params = new URLSearchParams(window.location.search)
              const owner = params.get('owner') || ''
              const key = params.get('key') || ''
              const hash = owner && key ? `?owner=${owner}&key=${key}` : ''
              window.open(`https://symbols.app/pricing${hash}`, '_blank', 'noopener')
              return
            }
            const rootState = el.getRootState()
            if (rootState.env === s.key) return
            rootState.update({ env: s.key, isLoadingProject: true })
            try {
              const { getProjectData } = await import('../../shared/functions/fetchProject.js')
              const project = await getProjectData({ env: s.key })
              rootState.update({ data: project, isLoadingProject: false }, { forcePreviewUpdate: true })
            } catch (e) {
              console.error('[preview] Failed to reload project:', e)
              rootState.update({ isLoadingProject: false })
            }
          }
        }
      }
    }
  },

  ResponsiveSwitching: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    color: 'dim',
    fontSize: 'Z1',
    align: 'center',
    children: () => [
      {
        key: 'screenL',
        icon: 'deviceBigScreen',
        title: 'Large Screen (1920×1024)'
      },
      {
        key: 'screenM',
        icon: 'deviceSmallScreen',
        title: 'Medium Screen (1680×1024)'
      },
      {
        key: 'tabletL',
        icon: 'deviceTabletLandscape',
        title: 'Tablet Landscape (1366×926)'
      },
      {
        key: 'tabletS',
        icon: 'deviceTabletPortrait',
        title: 'Tablet Portrait (1024×768)'
      },
      { key: 'mobileXS', icon: 'deviceMobile', title: 'Mobile (375×768)' },
      { key: 'resizable', icon: 'dimensionsAlt', title: 'Resizable' }
    ],
    childProps: {
      title: (el, s) => s.title,
      onClick: (ev, el, s) => {
        const parent = el.parent
        const scope = parent.scope
        if (!scope) return
        const newSize = scope.previewSize !== s.key ? s.key : null
        scope.previewSize = newSize

        // Update button icons
        for (const key of parent.__ref.__children) {
          const child = parent[key]
          if (!child?.Icon?.node) continue
          const isActive = child.state.key === newSize
          const iconBase = child.state.icon
          const outlineId = iconBase + 'Outline'
          const hasOutline =
            !!child.Icon.node.ownerDocument.getElementById(outlineId)
          const spriteId = isActive
            ? iconBase
            : hasOutline
              ? outlineId
              : iconBase
          const useEl = child.Icon.node.querySelector('use')
          if (useEl) useEl.setAttribute('xlink:href', '#' + spriteId)
          child.update()
        }

        el.getRootState().update({ previewSize: newSize })
      }
    }
  },


  ThemeSwitcher: {
    extends: 'NavbarButtonSet',
    margin: '- - - auto',
    gap: 'W2',
    childrenAs: 'state',
    children: [
      { key: 'light', icon: 'sun', title: 'Light mode' },
      { key: 'dark', icon: 'moon', title: 'Dark mode' }
    ],
    childProps: {
      theme: 'sepia',
      title: (el, s) => s.title,
      icon: (el, s) =>
        el.getRootState('sceneTheme') === s.key ? s.icon : s.icon + ' outline',
      isActive: (el, s) => el.getRootState('sceneTheme') === s.key,
      onClick: (ev, el, s) => {
        const newKey = s.key
        el.getRootState().update({ sceneTheme: newKey })
        const cfg = getProjectConfig(el)
        if (cfg) changeGlobalTheme(newKey, cfg)
        // Mirror ResponsiveSwitching: swap sprite hrefs manually since
        // reactive icon props don't re-evaluate on parent state change.
        const parent = el.parent
        for (const k of parent.__ref.__children) {
          const child = parent[k]
          if (!child?.Icon?.node) continue
          const base = child.state.icon
          const outlineId = base + 'Outline'
          const isActive = child.state.key === newKey
          const ownerDoc = child.Icon.node.ownerDocument
          const hasOutline = !!ownerDoc.getElementById(outlineId)
          const spriteId = isActive ? base : hasOutline ? outlineId : base
          const useEl = child.Icon.node.querySelector('use')
          if (useEl) useEl.setAttribute('xlink:href', '#' + spriteId)
        }
      }
    }
  },

  A11yFilter: {
    ...dropdownTrigger('a11y'),
    title: 'Accessibility simulation',
    StartButtons: {
      children: [{ icon: 'accessibility' }]
    },
    Value: {
      Text: {
        text: (el, s) => {
          const active = s.a11yFilter || 'none'
          return a11yOptions.find((o) => o.key === active)?.label || 'None'
        }
      }
    },
    Dropdown: {
      ...dropdownVisible("a11y"),
      ListInDropdown: {
        childrenAs: 'state',
        children: a11yOptions.map((o) => ({ key: o.key, text: o.label })),
        childProps: {
          onClick: (ev, el, s) => {
            closeDropdown(el)
            const val = s.key
            el.getRootState().update({
              a11yFilter: val === 'none' ? null : val
            })
          }
        }
      }
    }
  },

  Meta: {
    extends: 'NavbarButtonSet',
    gap: 'W2',

    InNewTab: {
      icon: 'sf arrow up right',
      title: 'Open in new tab',
      // URL composition lives in @symbo.ls/channels mermaidUrl —
      // composes user-env (`--<env>` segment) + system-env (channel
      // apex) into the canonical published URL. Same source the
      // Mermaid edge worker, CLI publish, and project cards use.
      onClick: (ev, el, s) => {
        const { owner, key } = resolveProjectParams()
        if (!owner || !key) return
        const env = s.env || 'production'
        const url = mermaidUrl({ owner, key, env }, currentChannel())
        if (url) window.open(url)
      }
    },

    CodeToggle: {
      hide: true,
      icon: 'code',
      round: 'C1',
      padding: 'Y2',
      '.isCodeOpen': {
        theme: 'navbarButton-active'
      },
      isCodeOpen: (_, s) => s.isCodeOpen,
      onClick: (ev, el, s) => {
        s.update({ isCodeOpen: !s.isCodeOpen })
        el.getRoot().update()
      }
    }
  }
}
